'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { VStack, button } from '@terra/ui-styled-system';
import { FileUpload } from '../../../components/file-upload';
import { BodyDefault, MetaDefault } from '../../../components/typography';
import {
  MAX_APPLICATION_DOCS_FILES,
  MAX_APPLICATION_DOCS_FILE_SIZE_BYTES,
} from './applicationDocumentation.constants';
import {
  formatAcceptedFileTypes,
  toDocumentationDisplayFiles,
} from './applicationDocumentationFiles.utils';
import {
  activateFileUploadLabelOnKeyDown,
  applicationDocsChooseFilesLabelClassName,
  applicationDocsFileUploadRootClassName,
} from './applicationDocumentationUpload.styles';
import { ApplicationDocumentationFileList } from './ApplicationDocumentationFileList';
import { useDocumentationFileRejectToast } from './useDocumentationFileRejectToast';

export type ApplicationDocumentationFileUploadAreaProps = {
  accept: string;
  files: File[];
  fileUploadKey: number;
  onFileAccept: (details: { files: File[] }) => void;
  onRemoveFile: ({ fileIndex }: { fileIndex: number }) => void;
  deleteAriaLabel: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  fileUploadRootKeySuffix?: string;
  hideSelectedFilesList?: boolean;
  scrollableFileList?: boolean;
};

export const ApplicationDocumentationFileUploadArea = ({
  accept,
  files,
  fileUploadKey,
  onFileAccept,
  onRemoveFile,
  deleteAriaLabel,
  disabled,
  isReadOnly,
  fileUploadRootKeySuffix = '',
  hideSelectedFilesList,
  scrollableFileList,
}: ApplicationDocumentationFileUploadAreaProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const handleFileReject = useDocumentationFileRejectToast();
  const fileUploadRootKey = `${fileUploadKey}${fileUploadRootKeySuffix}`;
  const hasReachedFileLimit = files.length >= MAX_APPLICATION_DOCS_FILES;
  const isUploadDisabled = hasReachedFileLimit || disabled || isReadOnly;
  const supportedFileTypesHint = useMemo(
    () =>
      t('supportedFileTypes', {
        formats: formatAcceptedFileTypes(accept),
      }),
    [accept, t],
  );

  return (
    <VStack alignItems="stretch" gap={6} w="full">
      <FileUpload.Root
        key={fileUploadRootKey}
        className={applicationDocsFileUploadRootClassName}
        accept={accept}
        defaultAcceptedFiles={files}
        maxFiles={MAX_APPLICATION_DOCS_FILES}
        maxFileSize={MAX_APPLICATION_DOCS_FILE_SIZE_BYTES}
        onFileAccept={onFileAccept}
        onFileReject={handleFileReject}
        disabled={isUploadDisabled}
        w="full"
        h="max-content"
      >
        <FileUpload.Dropzone
          disableClick
          w="full"
          h="max-content"
          minH={0}
          py="32px"
          px={0}
          borderRadius="2xl"
        >
          <FileUpload.Label
            role="button"
            tabIndex={isUploadDisabled ? -1 : 0}
            onKeyDown={activateFileUploadLabelOnKeyDown}
            w="full"
            cursor="pointer"
            className={applicationDocsChooseFilesLabelClassName}
          >
            <VStack gap={2} alignItems="center" pointerEvents="none">
              <BodyDefault textAlign="center">
                {t('dropFilesLabel')}
              </BodyDefault>
              <BodyDefault
                className={button({ size: 'sm', variant: 'outline' })}
              >
                {t('chooseFiles')}
              </BodyDefault>
              <MetaDefault color="fg.neutral.muted" textAlign="center">
                {supportedFileTypesHint}
              </MetaDefault>
            </VStack>
          </FileUpload.Label>
        </FileUpload.Dropzone>
        <FileUpload.HiddenInput />
      </FileUpload.Root>

      {hideSelectedFilesList ? null : (
        <ApplicationDocumentationFileList
          files={toDocumentationDisplayFiles(files)}
          onRemoveFile={onRemoveFile}
          deleteAriaLabel={deleteAriaLabel}
          isReadOnly={isReadOnly}
          scrollable={scrollableFileList}
        />
      )}
    </VStack>
  );
};
